import React from "react";
import {
  Box,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
  TextField,
  IconButton,
  useTheme,
} from "@mui/material";
import { Twitter, LinkedIn, YouTube } from "@mui/icons-material";
import { GradientButton } from "../RegisterTalentForm/RegisterTalentForm";
import { Link as RouterLink } from "react-router-dom"; // for SPA navigation
import { StageLinkLogo } from "../Branding/StageLinkLogo";
const footerLinks = {
  about: [
    { label: "Home", href: "/home" },
    { label: "Company", href: "/coming-soon" },
    { label: "Privacy", href: "/coming-soon" },
    { label: "Terms", href: "/coming-soon" },
  ],
  help: [
    { label: "Contact", href: "/coming-soon" },
    { label: "Support", href: "/coming-soon" },
    { label: "FAQs", href: "/coming-soon" },
  ],
};

const socialIcons = [
  { label: "Twitter", icon: <Twitter />, href: "https://twitter.com" },
  { label: "LinkedIn", icon: <LinkedIn />, href: "https://linkedin.com" },
  { label: "YouTube", icon: <YouTube />, href: "https://youtube.com" },
];

const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.72)",
  red: "#E50914",
  redHover: "#b20710",
};

const Footer = () => {
  const theme = useTheme();

  return (
    <Box
      component="footer"
      sx={{
        background:
          "linear-gradient(180deg, rgba(10,10,10,0.98), rgba(10,10,10,0.92))",
        pt: { xs: 6, md: 8 },
        pb: 4,
        mt: { xs: 6, md: 10 },
        borderTop: `1px solid ${COLORS.border}`,
        color: COLORS.text,
        fontFamily: '"Lexend", sans-serif',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Brand + Newsletter */}
          <Grid item xs={12} md={4}>
            <StageLinkLogo/>
            {/* Newsletter */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                mt: 2.5,
                maxWidth: 360,
              }}
            >
              <TextField
                placeholder="Your email"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  sx: {
                    bgcolor: COLORS.panel,
                    color: COLORS.text,
                    borderRadius: 2,
                    "& fieldset": { borderColor: COLORS.border },
                    "&:hover fieldset": { borderColor: COLORS.textMuted },
                    "&.Mui-focused fieldset": { borderColor: COLORS.red },
                  },
                }}
                inputProps={{
                  sx: {
                    "::placeholder": { color: "rgba(255,255,255,0.5)" },
                  },
                }}
              />
              
            </Box>
          </Grid>

          {/* About Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Stack spacing={1.2}>
              <Typography
                variant="subtitle2"
                sx={{ color: COLORS.text, fontWeight: 800, mb: 0.5 }}
              >
                About
              </Typography>
              {footerLinks.about.map((item) => (
                <MuiLink
                  key={item.label}
                  component={RouterLink}
                  to={item.href}
                  underline="none"
                  sx={{
                    fontSize: 14,
                    color: COLORS.textMuted,
                    "&:hover": { color: COLORS.text },
                  }}
                >
                  {item.label}
                </MuiLink>
              ))}
            </Stack>
          </Grid>

          {/* Help Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Stack spacing={1.2}>
              <Typography
                variant="subtitle2"
                sx={{ color: COLORS.text, fontWeight: 800, mb: 0.5 }}
              >
                Help
              </Typography>
              {footerLinks.help.map((item) => (
                <MuiLink
                  key={item.label}
                  component={RouterLink}
                  to={item.href}
                  underline="none"
                  sx={{
                    fontSize: 14,
                    color: COLORS.textMuted,
                    "&:hover": { color: COLORS.text },
                  }}
                >
                  {item.label}
                </MuiLink>
              ))}
            </Stack>
          </Grid>

          {/* Social */}
          <Grid item xs={12} sm={4} md={4}>
            <Typography
              variant="subtitle2"
              sx={{ color: COLORS.text, fontWeight: 800, mb: 1 }}
            >
              Stay Connected
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: COLORS.textMuted, mb: 2, maxWidth: 380 }}
            >
              Follow along for live announcements, drops, and behind-the-scenes.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              {socialIcons.map((item) => (
                <IconButton
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.label}
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    bgcolor: COLORS.panel,
                    color: COLORS.text,
                    border: `1px solid ${COLORS.border}`,
                    transition: "all .2s ease",
                    "&:hover": {
                      bgcolor: "rgba(229,9,20,0.12)",
                      borderColor: COLORS.red,
                      transform: "translateY(-2px)", 
                    },
                  }}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 3.5, md: 4 }, borderColor: COLORS.border }} />

        {/* Bottom bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 2 },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: COLORS.textMuted, order: { xs: 2, sm: 1 } }}
          >
            © {new Date().getFullYear()} StageLink. All rights reserved.
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            sx={{ order: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 0 } }}
          >
            <MuiLink
              component={RouterLink}
              to="/privacy"
              underline="none"
              sx={{ color: COLORS.textMuted, fontSize: 12, "&:hover": { color: COLORS.text } }}
            >
              Privacy
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to="/terms"
              underline="none"
              sx={{ color: COLORS.textMuted, fontSize: 12, "&:hover": { color: COLORS.text } }}
            >
              Terms
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to="/contact"
              underline="none"
              sx={{ color: COLORS.textMuted, fontSize: 12, "&:hover": { color: COLORS.text } }}
            >
              Contact
            </MuiLink>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;